import React from 'react';
import { InfoIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { SectionHeading } from './SectionHeading';
import { pathwaySteps } from '../data/painPoints';
export function IntroSection() {
  return <section className="bg-white px-4 py-16 sm:py-24" data-id="element-180" data-mp="3f5xjrc2mcpkw">
      <SectionHeading eyebrow="A new way to organize sensory modulation cases" title={<>
            You Don’t Always Need More Theory. Sometimes, You Need a{' '}
            <em className="text-brand" data-id="element-182" data-mp="3t71m1tdu1huj">Clearer Way to Organize</em> What You’re Seeing.
          </>} data-id="element-181" data-mp="3k6bwh7f60np9" />

      <div className="mx-auto mt-10 max-w-2xl space-y-5 text-[17px] leading-relaxed text-muted sm:text-lg" data-id="element-183" data-mp="1s41ylaqb4t7">
        <p data-id="element-184" data-mp="6rysuoui7ue9">If you’re a Pediatric Occupational Therapist, you probably know the feeling.</p>
        <p data-id="element-185" data-mp="brl26n77oqhs">
          You observe a child responding strongly to certain situations. You notice behaviors that seem to repeat. You collect information
          from sessions, families, and everyday routines.
        </p>
        <p data-id="element-186" data-mp="l2lyd7kcw3xx">But when you sit down to organize everything, you may find yourself asking:</p>
        <blockquote className="border-l-4 border-brand bg-brand-soft px-5 py-4 font-serif text-[22px] font-bold italic leading-snug text-ink sm:text-2xl" data-id="element-187" data-mp="g1of0rnm13uu">
          “How do I connect these observations without jumping to conclusions?”
        </blockquote>
        <p data-id="element-188" data-mp="13saomnd9iw6y">
          That’s exactly why I created the <strong className="text-ink" data-id="element-189" data-mp="13r93tr6g2vc1">Sensory Modulation Clinical Pathway™</strong>.
        </p>
        <p data-id="element-190" data-mp="yu8w7bty5kit">
          I wanted to create a practical educational resource that would help you organize the information you already have, recognize what
          is still missing, and approach sensory modulation questions through a more structured reasoning process.
        </p>
        <p data-id="element-191" data-mp="1mjbmt96k6npg">
          Instead of giving you another massive collection of disconnected theory, I created a visual system you can return to whenever a case
          feels difficult to organize.
        </p>
        <p className="text-ink" data-id="element-192" data-mp="3bznljcq9tuxv">
          <strong data-id="element-193" data-mp="3c0fiorzio0q9">The goal isn’t to replace your clinical judgment.</strong> It’s to give your clinical reasoning a clearer structure.
        </p>
      </div>

      <div className="mx-auto mt-14 max-w-5xl rounded-3xl bg-brand-dark px-5 py-10 text-center sm:px-10 sm:py-14" data-id="element-194" data-mp="32annpwni95l9">
        <h3 className="font-serif text-[26px] font-extrabold leading-tight text-white sm:text-4xl" data-id="element-195" data-mp="3785lqrzzi0eh">The Sensory Modulation Clinical Pathway™</h3>
        <p className="mx-auto mt-3 max-w-xl text-[17px] leading-relaxed text-white/80" data-id="element-196" data-mp="2tbip6qby1c93">
          From scattered observations to a structured working hypothesis in approximately <strong className="text-white" data-id="element-197" data-mp="2jcjmdodv0au0">15 minutes</strong>.
        </p>

        <ol className="mt-8 grid grid-cols-1 gap-3 text-left sm:grid-cols-2 lg:grid-cols-3" data-id="element-198" data-mp="2o9jjh9sqe38z">
          {pathwaySteps.map((step, i) => <li key={step} className="flex items-center gap-4 rounded-2xl bg-white/10 px-4 py-4" data-id="element-199" data-mp="hkog5oadgyj6" data-mpi={i}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white font-serif text-lg font-extrabold text-brand-dark" data-id="element-200" data-mp="mi6slonllvvx">
                {i + 1}
              </span>
              <span className="text-[16px] font-semibold text-white" data-id="element-201" data-mp="223ijz5cwjr2l">{step}</span>
            </li>)}
        </ol>

        <p className="mx-auto mt-6 max-w-xl text-[15px] leading-relaxed text-white/80" data-id="element-202" data-mp="2gswp219g3mc6">
          A practical pathway to help you organize the information available in a case and identify the questions that deserve further
          exploration.
        </p>
        <p className="mx-auto mt-4 flex max-w-xl items-start gap-2 text-left text-[12px] leading-relaxed text-white/60" data-id="element-203" data-mp="2lovdtpyor6d4">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-204" data-mp="2zowo1w0ubq59" />
          The 15-minute timeframe refers to a focused organization exercise, not a complete clinical assessment or a guaranteed diagnostic
          conclusion.
        </p>
      </div>

      <CTAButton className="mt-10" label="I want a clearer reasoning system" data-id="element-205" data-mp="10ddepgbmkfv2" />
    </section>;
}